import { useMemo, useState } from "react";
import { type Course, GRADES, gpa } from "@/lib/math/education";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";

interface Row extends Course {
  name: string;
}

const EMPTY: Row = { name: "", grade: "A", credits: 3 };

export function GpaCalc() {
  const locale = getLocale();
  const [rows, setRows] = useState<Row[]>([
    { name: "", grade: "A", credits: 3 },
    { name: "", grade: "B+", credits: 4 },
    { name: "", grade: "A-", credits: 3 },
  ]);

  const result = useMemo(() => gpa(rows), [rows]);

  const update = (i: number, patch: Partial<Row>) =>
    setRows((r) =>
      r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)),
    );
  const remove = (i: number) =>
    setRows((r) => (r.length > 1 ? r.filter((_, idx) => idx !== i) : r));

  return (
    <div className="sci-card rounded-2xl space-y-4 p-6 sm:p-8">
      <div className="space-y-2">
        <div className="hidden grid-cols-[1fr_auto_auto_auto] gap-2 px-1 text-xs text-muted-foreground sm:grid">
          <span>{m.gpa_course()}</span>
          <span>{m.gpa_grade()}</span>
          <span>{m.gpa_credits()}</span>
          <span />
        </div>
        {rows.map((row, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional and reorder-free
          <div key={i} className="grid grid-cols-[1fr_auto_auto_auto] gap-2">
            <input
              type="text"
              aria-label={`${m.gpa_course()} ${i + 1}`}
              placeholder={m.gpa_course()}
              value={row.name}
              onChange={(e) => update(i, { name: e.target.value })}
              className="min-w-0 rounded-md border border-black/15 bg-white px-3 py-2 text-sm text-neutral-900"
            />
            <select
              aria-label={`${m.gpa_grade()} ${i + 1}`}
              value={row.grade}
              onChange={(e) => update(i, { grade: e.target.value })}
              className="rounded-md border border-black/15 bg-white px-2 py-2 text-sm text-neutral-900"
            >
              {GRADES.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
            <input
              type="number"
              inputMode="decimal"
              aria-label={`${m.gpa_credits()} ${i + 1}`}
              value={row.credits}
              min={0}
              onChange={(e) => update(i, { credits: Number(e.target.value) })}
              className="w-20 rounded-md border border-black/15 bg-white px-3 py-2 text-sm text-neutral-900"
            />
            <button
              type="button"
              aria-label={m.gpa_remove()}
              onClick={() => remove(i)}
              className="sci-panel rounded-md px-3 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => setRows((r) => [...r, { ...EMPTY }])}
        className="sci-panel rounded-md px-4 py-2 text-sm font-medium transition-colors hover:text-foreground"
      >
        {m.gpa_add()}
      </button>

      {result != null ? (
        <div className="sci-result-zone flex items-baseline justify-between gap-4 rounded-xl px-5 py-4">
          <span className="text-sm font-medium text-muted-foreground">
            {m.gpa_label()}
          </span>
          <span
            data-testid="gpa-result"
            className="sci-result font-display text-3xl font-bold sm:text-4xl"
          >
            {result.toLocaleString(locale, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{m.gpa_hint()}</p>
      )}
    </div>
  );
}
